import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Star, Trash2, ClipboardCheck } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const ratingLabels = { 1: "Needs Improvement", 2: "Below Expectations", 3: "Meets Expectations", 4: "Exceeds Expectations", 5: "Outstanding" };
const statusColors = {
  draft: "bg-slate-100 text-slate-600 border-slate-300",
  submitted: "bg-[#EFF6FF] text-[#1E40AF] border-[#3B82F6]",
  acknowledged: "bg-[#ECFDF5] text-[#065F46] border-[#10B981]",
};

export default function PerformancePage() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [form, setForm] = useState({ employee_id: "", employee_name: "", employee_email: "", review_period: "", rating: "3", goals: "", achievements: "", areas_for_improvement: "", comments: "", status: "draft" });

  const isManager = ["admin", "hr_manager"].includes(user?.role);

  useEffect(() => {
    API.get("/performance/reviews").then(({ data }) => setReviews(data)).catch(console.error).finally(() => setLoading(false));
    if (isManager) API.get("/employees").then(({ data }) => setEmployees(data)).catch(console.error);
  }, [isManager]);

  const createReview = async (e) => {
    e.preventDefault();
    try {
      const emp = employees.find((e) => e.id === form.employee_id);
      await API.post("/performance/reviews", { ...form, rating: parseInt(form.rating), employee_name: emp?.name || "", employee_email: emp?.email || "" });
      toast.success("Review created");
      setCreateDialog(false);
      setForm({ employee_id: "", employee_name: "", employee_email: "", review_period: "", rating: "3", goals: "", achievements: "", areas_for_improvement: "", comments: "", status: "draft" });
      API.get("/performance/reviews").then(({ data }) => setReviews(data));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteReview = async (rid) => {
    if (!window.confirm("Delete this review?")) return;
    await API.delete(`/performance/reviews/${rid}`);
    toast.success("Deleted");
    API.get("/performance/reviews").then(({ data }) => setReviews(data));
  };

  const acknowledgeReview = async (rid) => {
    await API.put(`/performance/reviews/${rid}`, { status: "acknowledged" });
    toast.success("Review acknowledged");
    API.get("/performance/reviews").then(({ data }) => setReviews(data));
    setDetailDialog(false);
  };

  const viewDetail = (r) => { setSelectedReview(r); setDetailDialog(true); };

  const RatingStars = ({ rating }) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-3.5 w-3.5 ${i <= rating ? "text-[#F59E0B] fill-[#F59E0B]" : "text-slate-300"}`} />)}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-950" style={{ fontFamily: "Outfit, sans-serif" }}>Performance Reviews</h1>
        {isManager && (
          <button data-testid="create-review-btn" onClick={() => setCreateDialog(true)} className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm flex items-center gap-2">
            <Plus className="h-4 w-4" /> New Review
          </button>
        )}
      </div>

      {loading ? <div className="text-center py-12 text-sm text-slate-500">Loading...</div> : reviews.length === 0 ? (
        <div className="text-center py-12 border border-slate-200 rounded-sm">
          <ClipboardCheck className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No performance reviews yet</p>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Employee</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden sm:table-cell">Period</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden md:table-cell">Reviewer</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Rating</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Status</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reviews.map((r) => (
                <TableRow key={r.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => viewDetail(r)} data-testid={`review-row-${r.id}`}>
                  <TableCell className="px-4 py-3"><div className="text-sm font-medium text-slate-900">{r.employee_name}</div></TableCell>
                  <TableCell className="px-4 py-3 text-sm text-slate-700 hidden sm:table-cell">{r.review_period}</TableCell>
                  <TableCell className="px-4 py-3 text-sm text-slate-700 hidden md:table-cell">{r.reviewer_name}</TableCell>
                  <TableCell className="px-4 py-3"><RatingStars rating={r.rating} /></TableCell>
                  <TableCell className="px-4 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium border ${statusColors[r.status] || ""}`}>{r.status}</span></TableCell>
                  <TableCell className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {isManager && <button onClick={() => deleteReview(r.id)} className="p-1.5 hover:bg-red-50 rounded-sm text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create Review Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create Performance Review</DialogTitle><DialogDescription>Evaluate employee performance</DialogDescription></DialogHeader>
          <form onSubmit={createReview} className="space-y-3 py-2" data-testid="review-form">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Employee</label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="review-employee-select" className="h-9"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} — {e.department}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Review Period</label>
              <input data-testid="review-period" value={form.review_period} onChange={(e) => setForm({ ...form, review_period: e.target.value })} placeholder="e.g. Q1 2026, Annual 2025" required className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full text-sm" /></div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Rating (1-5)</label>
              <Select value={form.rating} onValueChange={(v) => setForm({ ...form, rating: v })}>
                <SelectTrigger data-testid="review-rating" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{[1,2,3,4,5].map((r) => <SelectItem key={r} value={String(r)}>{r} — {ratingLabels[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Goals</label>
              <textarea data-testid="review-goals" value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} rows={2} className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full text-sm resize-none" placeholder="Key goals and objectives" /></div>
            <div><label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Achievements</label>
              <textarea data-testid="review-achievements" value={form.achievements} onChange={(e) => setForm({ ...form, achievements: e.target.value })} rows={2} className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full text-sm resize-none" placeholder="Key accomplishments" /></div>
            <div><label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Areas for Improvement</label>
              <textarea data-testid="review-improvement" value={form.areas_for_improvement} onChange={(e) => setForm({ ...form, areas_for_improvement: e.target.value })} rows={2} className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full text-sm resize-none" /></div>
            <div><label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Comments</label>
              <textarea data-testid="review-comments" value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} rows={2} className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full text-sm resize-none" /></div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Status</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="submitted">Submitted</SelectItem></SelectContent>
              </Select>
            </div>
            <DialogFooter className="pt-2">
              <button type="button" onClick={() => setCreateDialog(false)} className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-4 py-2 text-sm">Cancel</button>
              <button type="submit" data-testid="review-submit" className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-4 py-2 text-sm">Save Review</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          {selectedReview && (
            <>
              <DialogHeader><DialogTitle>Performance Review — {selectedReview.employee_name}</DialogTitle><DialogDescription>{selectedReview.review_period}</DialogDescription></DialogHeader>
              <div className="space-y-4 py-2 text-sm">
                <div className="flex items-center gap-3">
                  <RatingStars rating={selectedReview.rating} />
                  <span className="text-slate-600">{ratingLabels[selectedReview.rating]}</span>
                  <span className={`ml-auto inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-medium border ${statusColors[selectedReview.status] || ""}`}>{selectedReview.status}</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><span className="text-slate-500 block">Reviewer</span><span className="text-slate-900">{selectedReview.reviewer_name}</span></div>
                  <div><span className="text-slate-500 block">Date</span><span className="text-slate-900">{selectedReview.created_at?.split("T")[0]}</span></div>
                </div>
                {selectedReview.goals && <div><span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1">Goals</span><p className="text-slate-700">{selectedReview.goals}</p></div>}
                {selectedReview.achievements && <div><span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1">Achievements</span><p className="text-slate-700">{selectedReview.achievements}</p></div>}
                {selectedReview.areas_for_improvement && <div><span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1">Areas for Improvement</span><p className="text-slate-700">{selectedReview.areas_for_improvement}</p></div>}
                {selectedReview.comments && <div><span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1">Comments</span><p className="text-slate-700">{selectedReview.comments}</p></div>}
                {selectedReview.status === "submitted" && !isManager && (
                  <button onClick={() => acknowledgeReview(selectedReview.id)} className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-4 py-2 text-sm w-full">Acknowledge Review</button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
